/**
 * VM Runtime Panel - USB and Drive Hotplug
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, notificationDialog } from '../ui/dialogs.js';

interface UsbDevice {
  vendor_id: string;
  product_id: string;
  bus: string;
  device: string;
  description: string;
}

interface AttachedDrive {
  path: string;
  interface: string;
}

/**
 * Show runtime panel for USB and drive management
 * @param vmId - VM ID
 * @param host - Target host where the VM runs (for delegated VMs)
 */
export async function showRuntimePanel(vmId: string, host?: string): Promise<void> {
  declareLoading();
  const [hostUsb, vmInfo] = await Promise.all([
    apiCall(api.getHostUsbDevices(host), 'Failed to Load USB Devices'),
    apiCall(api.getVmStatus(vmId), 'Failed to Load VM Status'),
  ]);
  declareLoaded();
  if (!hostUsb || !vmInfo) return;

  {

    const info = vmInfo as unknown as Record<string, unknown>;
    const attachedUsb = (info.usb_devices as UsbDevice[]) || [];
    const attachedDrives = (info.drives as AttachedDrive[]) || [];

    // Build USB devices list
    let usbHtml = '<div style="max-height:150px;overflow-y:auto;border:1px solid #ccc;padding:5px;">';
    for (const usb of (hostUsb as UsbDevice[])) {
      const key = `${usb.vendor_id}:${usb.product_id}`;
      const isAttached = attachedUsb.some(
        (a) => a.vendor_id === usb.vendor_id && a.product_id === usb.product_id
      );
      const checked = isAttached ? 'checked' : '';
      usbHtml += `<label style="display:block;margin:3px 0;">
        <input type="checkbox" class="usb-device" data-vendor="${escapeAttr(usb.vendor_id)}" data-product="${escapeAttr(usb.product_id)}" data-desc="${escapeAttr(usb.description)}" ${checked}>
        Bus ${escapeHtml(usb.bus)} Dev ${escapeHtml(usb.device)}: ${escapeHtml(usb.description)} (${escapeHtml(key)})
      </label>`;
    }
    usbHtml += '</div>';

    // Build attached drives list
    let drivesHtml =
      '<table style="width:100%"><thead><tr><th>Path</th><th>Interface</th><th></th></tr></thead><tbody>';
    attachedDrives.forEach((drive, idx) => {
      drivesHtml += `<tr>
        <td>${escapeHtml(drive.path)}</td>
        <td>${escapeHtml(drive.interface || 'virtio')}</td>
        <td><button class="ui-button unplug-drive-btn" data-idx="${idx}">Detach</button></td>
      </tr>`;
    });
    drivesHtml += '</tbody></table>';

    const form = `
      <fieldset>
        <legend>USB Devices</legend>
        ${usbHtml}
        <button class="ui-button" id="refresh-usb-btn">Refresh</button>
        <button class="ui-button" id="apply-usb-btn">Apply Changes</button>
      </fieldset>
      <fieldset>
        <legend>Attached Drives</legend>
        ${drivesHtml}
        <button class="ui-button" id="hotplug-drive-btn">+ Hotplug Drive</button>
      </fieldset>
    `;

    $('<div id="runtime-dialog"></div>')
      .appendTo('body')
      .html('<div>' + form + '</div>')
      .dialog({
        modal: true,
        title: 'Runtime: VM ' + vmId,
        width: 600,
        autoOpen: true,
        buttons: {
          Close: function () {
            $(this).dialog('close');
          },
        },
        close: function () {
          $(this).remove();
        },
        open: function () {
          $('#refresh-usb-btn').on('click', () => runtimeRefreshUsb(vmId, host));
          $('#apply-usb-btn').on('click', () => runtimeApplyUsb(vmId, attachedUsb));
          $('#hotplug-drive-btn').on('click', () => runtimeHotplugDrive(vmId, host));
          $('.unplug-drive-btn').on('click', function () {
            const idx = parseInt($(this).data('idx'));
            runtimeUnplugDrive(vmId, idx, host);
          });
        },
      });
  }
}

async function runtimeRefreshUsb(vmId: string, host?: string): Promise<void> {
  $('#runtime-dialog').dialog('close');
  await showRuntimePanel(vmId, host);
}

async function runtimeApplyUsb(vmId: string, currentlyAttached: UsbDevice[]): Promise<void> {
  declareLoading();

  // Get checked USB devices
  const wantAttached: UsbDevice[] = [];
  $('.usb-device').each(function () {
    if ($(this).is(':checked')) {
      wantAttached.push({
        vendor_id: $(this).data('vendor'),
        product_id: $(this).data('product'),
        description: $(this).data('desc'),
        bus: '',
        device: '',
      });
    }
  });

  // Detach devices that are no longer checked
  for (const usb of currentlyAttached) {
    if (!wantAttached.some((w) => w.vendor_id === usb.vendor_id && w.product_id === usb.product_id)) {
      try {
        await api.detachUsbDevice(vmId, usb.vendor_id, usb.product_id);
      } catch (e) {
        console.error('Detach failed:', e);
      }
    }
  }

  // Attach newly checked devices
  for (const usb of wantAttached) {
    if (!currentlyAttached.some((c) => c.vendor_id === usb.vendor_id && c.product_id === usb.product_id)) {
      try {
        await api.attachUsbDevice(vmId, usb.vendor_id, usb.product_id, usb.description);
      } catch (e) {
        console.error('Attach failed:', e);
      }
    }
  }

  declareLoaded();
  notificationDialog('USB devices updated');
}

/**
 * Show hotplug drive dialog
 * @param vmId - VM ID
 * @param host - Target host to fetch disk images from (for delegated VMs)
 */
export async function runtimeHotplugDrive(vmId: string, host?: string): Promise<void> {
  const [options, availableDrives] = await Promise.all([
    apiCall(api.getOptions(), 'Failed to Load Options'),
    apiCall(api.getSecondaryDisks(host), 'Failed to Load Drives'),
  ]);
  if (!options || !availableDrives) return;

  const interfaces = options.drive_interfaces || {};

  let pathOptions = '<option value="">Select drive...</option>';
  for (const [value, label] of Object.entries(availableDrives as Record<string, string>)) {
    if (value !== 'none') {
      pathOptions += `<option value="${escapeAttr(value)}">${escapeHtml(label)}</option>`;
    }
  }

  let interfaceOptions = '';
  for (const [value, label] of Object.entries(interfaces)) {
    if (value !== 'ide') {
      // IDE doesn't support hotplug
      interfaceOptions += `<option value="${escapeAttr(value)}">${escapeHtml(String(label).split('(')[0].trim())}</option>`;
    }
  }

  const form = `
    <label>Drive Path:</label>
    <select id="hotplug-path">${pathOptions}</select><br><br>
    <label>Interface:</label>
    <select id="hotplug-interface">${interfaceOptions}</select><br><br>
    <label><input type="checkbox" id="hotplug-readonly"> Read-only</label>
  `;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Hotplug Drive</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Hotplug Drive',
      width: 400,
      buttons: {
        Attach: async function () {
          const path = $('#hotplug-path').val() as string;
          if (!path) {
            alert('Please select a drive');
            return;
          }
          declareLoading();
          const result = await apiCall(api.hotplugDrive(vmId, {
            path: path,
            interface: $('#hotplug-interface').val() as string,
            readonly: $('#hotplug-readonly').is(':checked'),
          }), 'Hotplug Failed');
          declareLoaded();
          if (result !== null) {
            $(this).dialog('close');
            $('#runtime-dialog').dialog('close');
            await showRuntimePanel(vmId);
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

async function runtimeUnplugDrive(vmId: string, driveIndex: number, host?: string): Promise<void> {
  if (!confirm('Detach this drive? Data may be lost if not properly unmounted in guest.')) {
    return;
  }
  declareLoading();
  const result = await apiCall(api.hotunplugDrive(vmId, driveIndex), 'Unplug Failed');
  declareLoaded();
  if (result !== null) {
    $('#runtime-dialog').dialog('close');
    await showRuntimePanel(vmId, host);
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).showRuntimePanel = showRuntimePanel;
}
